import React, { useEffect, useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';
import { LOGIN_PATH } from '@/config/paths';
import { UserLoggedoutModal } from '@/components/ui/userloggedoutmodal';
import { errorBus } from '@/api/errorBus';

export const decodeToken = (token: string) => {
  try {
    return jwtDecode(token);
  } catch (error) {
    console.error("Invalid token:", error);
    return null;
  }
};

function isTokenValid(token: string | null): boolean {
  if (!token) return false;
  const decoded = decodeToken(token);
  if (!decoded || !decoded.exp) return false;
  return decoded.exp * 1000 > Date.now();
}

export function AuthGuard({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [showSessionModal, setShowSessionModal] = useState(false);
  const token = localStorage.getItem('TOKEN');

  // Timer-based expiry: show session modal when token expires
  useEffect(() => {
    if (!token) return;
    const decoded = decodeToken(token);
    if (!decoded || !decoded.exp) return;

    const timeUntilExpiry = decoded.exp * 1000 - Date.now();

    if (timeUntilExpiry > 0) {
      const timeoutId = setTimeout(() => {
        localStorage.removeItem('TOKEN');
        localStorage.removeItem('user_details_from_server');
        setShowSessionModal(true);
      }, timeUntilExpiry);

      return () => clearTimeout(timeoutId);
    }
  }, [token]);

  // errorBus listener: react to interceptor's unauthorized event (session expiry)
  useEffect(() => {
    const unsubUnauthorized = errorBus.on('unauthorized', () => {
      setShowSessionModal(true);
    });

    return unsubUnauthorized;
  }, []);

  const handleCloseSessionModal = () => {
    setShowSessionModal(false);
    navigate(LOGIN_PATH, { replace: true });
  };

  if (!isTokenValid(token) && !showSessionModal) {
    localStorage.removeItem('TOKEN');
    localStorage.removeItem('user_details_from_server');
    return <Navigate to={LOGIN_PATH} replace state={{ from: `${location.pathname}${location.search}` }} />;
  }

  return (
    <>
      {children}

      {/* 401 — session expired / unauthorized */}
      <UserLoggedoutModal open={showSessionModal} onClose={handleCloseSessionModal} />
    </>
  );
}
